
import type { PropOptions } from 'vue';
import Vue from 'vue';
import ArrowDownIcon from './images/arrow-down.vue.svg';
import CheckIcon from './images/check-icon.vue.svg';
import Dropdown from '~/components/common/Dropdown/Dropdown.vue';
import DropdownMenu from '~/components/common/Dropdown/DropdownMenu.vue';
import DropdownToggler from '~/components/common/Dropdown/DropdownToggler.vue';
import DropdownItem from '~/components/common/Dropdown/DropdownItem.vue';

const CUSTOM_OPTION_TITLE = 'Other';

interface IData {
  isDropdownVisible: boolean;
  customOptionTitle: string;
}

interface IOption {
  key: string | number;
  title: string;
  isSelected?: boolean;
}

export default Vue.extend({
  name: 'JobsDropdown',
  components: {
    Dropdown,
    DropdownMenu,
    DropdownToggler,
    DropdownItem,
    ArrowDownIcon,
    CheckIcon,
  },
  props: {
    label: {
      type: String,
      default: '',
    } as PropOptions<string>,
    options: {
      type: Array,
      default: () => [],
    } as PropOptions<IOption[]>,
    multiselect: {
      type: Boolean,
      default: false,
    } as PropOptions<boolean>,
    placeholder: {
      type: String,
      required: true,
    } as PropOptions<string>,
    value: {
      type: [String, Number, Array],
      default: null,
    } as PropOptions<string | number | string[]>,
    hasCustomOption: {
      type: Boolean,
      default: false,
    } as PropOptions<boolean>,
    isCustomOptionSelected: {
      type: Boolean,
      default: false,
    } as PropOptions<boolean>,
    isValid: {
      type: Boolean,
      default: true,
    } as PropOptions<boolean>,
    hint: {
      type: String,
      default: '',
    } as PropOptions<string>,
    dropdownDirection: {
      type: String,
      default: 'bottom',
      validator(val: string) {
        return ['top', 'bottom'].includes(val);
      },
    },
  },
  data(): IData {
    return {
      isDropdownVisible: false,
      customOptionTitle: CUSTOM_OPTION_TITLE,
    };
  },
  computed: {
    visibleOptions(): IOption[] {
      if (!this.multiselect || !Array.isArray(this.value)) return this.options;

      return this.options.map((option: IOption) => ({
        ...option,
        isSelected: (this.value as string[]).includes(String(option.key)),
      }));
    },
    togglerClass(): Record<string, boolean> {
      return {
        'jobs-dropdown__toggler': true,
        'jobs-dropdown__toggler--error': !this.isValid,
      };
    },
    hasValue(): boolean {
      if (!this.value) return false;

      if (!this.multiselect) return !!String(this.value).trim();

      return !!(this.value as string[]).length;
    },
  },
  methods: {
    toggleDropdown(): void {
      this.isDropdownVisible = !this.isDropdownVisible;
    },
    handleSelect(key: string | number): void {
      if (!this.multiselect) {
        this.$emit('input', key);
        return;
      }

      let options;

      if ((this.value as string[]).includes(String(key))) {
        options = (this.value as string[]).filter(el => el !== key);
      } else {
        options = [...(this.value as string[]), key];
      }

      this.$emit('input', options);
    },
  },
});
